import React from 'react';
import type { FormValue, Question, OASISFormData } from '../types';
import type { LayoutNode } from './layout/types';
import { getLayoutCompileError } from './layout/tryParseLayoutJson';
import { BespokeLayoutRenderer } from './layout/BespokeLayoutRenderer';
import { BespokeSectionErrorBoundary } from './BespokeSectionErrorBoundary';

export interface SchemaQuestionOption {
  qid: string;
  label: string;
  category: string;
}

/**
 * Props that every bespoke form section component receives.
 */
export interface BespokeFormSectionProps {
  /** Current answers keyed by QID */
  answers: Record<string, FormValue>;
  /** Update a single answer by QID */
  onAnswerChange: (qid: string, value: FormValue) => void;
  /** Batch-update multiple answers */
  onFormDataChange: (updates: Partial<OASISFormData>) => void;
  /** The section's question schema for reference */
  schema: Question[];
  /** Flat list of all QIDs across all sections (for lookups / bespoke UIs) */
  schemaQuestionOptions: SchemaQuestionOption[];
  /** The section/category name */
  sectionName: string;
  /**
   * Rendered in place of the section if the bespoke layout throws at render
   * time (e.g. a malformed-but-known-type node the compile gate can't detect).
   * Callers should pass the standard non-bespoke grid so the form stays usable.
   */
  fallback?: React.ReactNode;
}

export type BespokeSectionComponent =
  React.ComponentType<BespokeFormSectionProps>;

// ---------------------------------------------------------------------------
// DB-backed bespoke config (JSON layout per section).
// ---------------------------------------------------------------------------

export interface BespokeConfigEntry {
  section_name: string;
  layout_json: LayoutNode[] | null;
}

/** Map shape held in React state: schemaVersionName → sectionName → entry */
export type BespokeDbConfig = ReadonlyMap<
  string,
  ReadonlyMap<string, BespokeConfigEntry>
>;

/**
 * Stable component cache keyed by layout array reference.
 *
 * `resolveBespokeSection` is called during render; without this cache every
 * call would produce a new function identity, causing React to unmount and
 * remount the bespoke section on every parent re-render.  A WeakMap is used
 * so cached entries are GC'd when the layout array itself is no longer held
 * (e.g. after a config reload replaces `bespokeDbConfig`).
 */
const _jsonComponentCache = new WeakMap<
  LayoutNode[],
  BespokeSectionComponent
>();

/**
 * Compile-error cache keyed by layout array reference, mirroring
 * `_jsonComponentCache`. `resolveBespokeSection` runs during render (every
 * keystroke), so without this the JSON round-trip + tree walk in
 * `getLayoutCompileError` would repeat on every render for each section.
 */
const _compileErrorCache = new WeakMap<LayoutNode[], string | null>();

function _cachedLayoutCompileError(layout: LayoutNode[]): string | null {
  let error = _compileErrorCache.get(layout);
  if (error === undefined) {
    error = getLayoutCompileError(layout);
    _compileErrorCache.set(layout, error);
  }
  return error;
}

/**
 * Every bespoke section is wrapped in {@link BespokeSectionErrorBoundary} here
 * so the protection can't be forgotten at a call site. The compile gate
 * (`getLayoutCompileError`) walks a different code path than the renderer, so a
 * malformed-but-known-type node can still throw during render; the boundary
 * degrades to `props.fallback` (the standard grid) instead of crashing the app.
 * Because each section's component is a distinct identity (keyed by layout
 * array), switching sections remounts the boundary, so a throw in one section
 * never sticks to another.
 */
function _getOrCreateJsonComponent(
  layout: LayoutNode[]
): BespokeSectionComponent {
  let comp = _jsonComponentCache.get(layout);
  if (!comp) {
    function BespokeJsonSection(props: BespokeFormSectionProps) {
      return React.createElement(BespokeSectionErrorBoundary, {
        fallback: props.fallback ?? _defaultBespokeFallback,
        children: React.createElement(BespokeLayoutRenderer, {
          ...props,
          layout,
        }),
      });
    }
    _jsonComponentCache.set(layout, BespokeJsonSection);
    comp = BespokeJsonSection;
  }
  return comp;
}

const _defaultBespokeFallback = React.createElement(
  'div',
  {
    className:
      'rounded border border-destructive/50 bg-destructive/10 px-3 py-2.5 text-sm text-destructive',
  },
  'This section could not be displayed.'
);

/**
 * `dbConfig` maps schema version name → section name → BespokeConfigEntry.
 * Pass the map held in React state for the form instance.
 */
export function resolveBespokeSection(
  dbConfig: BespokeDbConfig | null | undefined,
  schemaVersionName: string | undefined,
  sectionName: string
): BespokeSectionComponent | null {
  if (!schemaVersionName) return null;

  const entry = dbConfig?.get(schemaVersionName)?.get(sectionName);

  if (entry?.layout_json) {
    if (_cachedLayoutCompileError(entry.layout_json) !== null) {
      return null;
    }
    return _getOrCreateJsonComponent(entry.layout_json);
  }

  return null;
}

export function hasBespokeSection(
  dbConfig: BespokeDbConfig | null | undefined,
  schemaVersionName: string | undefined,
  sectionName: string
): boolean {
  return (
    resolveBespokeSection(dbConfig, schemaVersionName, sectionName) !== null
  );
}
